import api from '../../lib/api';
import type { CoverageTier, ProfileField, PublicationStatus, ResponseStatus, Stance } from '../../constants/candidateProfiles';
import { PROFILE_FIELDS } from '../../constants/candidateProfiles';
import type { EndorsementInput, EndorsementRow } from '../../constants/endorsements';
import { pickBundleFiles, selectBundleFiles, type ExplainerBundle } from '../pages/elections/explainerImport';

/**
 * Candidate profiles — the wire shapes the admin routes return and the calls
 * the cards and modals make (CANDIDATE_PROFILES_PRD.md §5.3, §5.4, §9). The
 * backend does all the parsing, matching and writing; the client picks files,
 * reads the preflight, and publishes.
 */

export type { ExplainerBundle as ProfileBundle };
export { pickBundleFiles, selectBundleFiles };

export interface ProvenanceEntry {
  origin: 'staff_written' | 'ai_reviewed' | 'official_source';
  tool?: string | null;
  source?: string | null;
  reviewed_by?: string | null;
  reviewed_at?: string | null;
}

export interface CandidateProfile {
  id: string;
  candidate_id: string;
  coverage_tier: CoverageTier;
  publication_status: PublicationStatus;
  profile_version: string | null;
  facts_as_of: string | null;
  who_they_are: string | null;
  why_running: string | null;
  top_priorities: string | null;
  experience_background: string | null;
  record_in_office: string | null;
  who_backs_them: string | null;
  content_provenance: Partial<Record<ProfileField, ProvenanceEntry>> | null;
  published_by: string | null;
  published_at: string | null;
  updated_by: string | null;
  updated_at: string;
}

export interface CandidateStatement {
  id: string;
  source_type: string;
  source_name: string;
  source_url: string | null;
  source_date: string | null;
  question: string | null;
  body: string | null;
  response_status: ResponseStatus;
  display_order: number;
}

/** The shared endorsement row (ENDORSEMENTS_PRD §5.1); candidates have no side. */
export type CandidateEndorsement = EndorsementRow;

export interface CandidatePosition {
  id: string;
  ballot_initiative_id: string;
  stance: Stance;
  quote: string | null;
  source_type: string | null;
  source_name: string | null;
  source_url: string | null;
  source_date: string;
  measure_number: string | null;
  popular_name: string | null;
  ballot_title: string | null;
  ballot_order: number | null;
  sos_initiative_number: string | null;
  initiative_cycle: string | null;
}

export interface CandidateDocument {
  id: string;
  kind: 'traceability_record' | 'other';
  label: string;
  version_label: string | null;
  original_name: string;
  mime_type: string;
  file_size: number | string;
  sha256: string;
  notes: string | null;
  uploaded_by: string | null;
  uploaded_by_name: string | null;
  uploaded_at: string;
}

export interface RunningMate {
  candidate_id: string;
  person_id: string;
  name: string;
  party: string | null;
  role: 'governor' | 'lt_governor';
}

/** A row added or corrected by hand (absent = exactly as imported). */
export interface EditMarker {
  updated_at: string;
  updated_by_name: string | null;
}

export interface CandidateProfileBundleView {
  profile: CandidateProfile | null;
  statements: CandidateStatement[];
  endorsements: CandidateEndorsement[];
  positions: CandidatePosition[];
  documents: CandidateDocument[];
  running_mate: RunningMate | null;
  edits?: {
    statements: Record<string, EditMarker>;
    endorsements: Record<string, EditMarker>;
    positions: Record<string, EditMarker>;
  };
}

export interface ProfileFieldPreview {
  column: string;
  label: string;
  current: string | null;
  incoming: string | null;
  action: 'replace' | 'keep' | 'clear' | 'empty';
  /** The stored text is declared staff-written — a replace overwrites a hand edit. */
  hand_edited?: boolean;
}

export interface ProfilePreflight {
  ok: boolean;
  blocking: string[];
  warnings: string[];
  candidate: {
    id: string; person_id: string; name: string; office_sought: string | null; district: string | null;
    party: string | null; status: string | null; election_year: number; election_type: string | null;
    contest_id: string | null; contest_label: string | null; office_key: string | null;
    publication_status: PublicationStatus | null;
  };
  version: { current: string | null; incoming: string | null };
  coverage: { current: CoverageTier | null; incoming: CoverageTier; contest: CoverageTier[] | null };
  fields: ProfileFieldPreview[];
  statements: { current: number; incoming: number; sources: string[] };
  endorsements: { current: number; incoming: number };
  positions: { current: number; incoming: number; resolved: number; expected: number; unresolved: string[]; cycle: string | null };
  running_mate: { current: string | null; incoming: { candidate_id: string; name: string } | null; problem: string | null } | null;
  gaps: string[];
  documents: string[];
}

export interface ProfileImportSummary {
  candidate_id: string;
  label: string;
  version: string | null;
  record_id: string | null;
  coverage_tier: CoverageTier;
  fields_written: string[];
  fields_cleared: string[];
  fields_kept: string[];
  statements: number;
  endorsements: number;
  positions: number;
  positions_unresolved: string[];
  running_mate: { candidate_id: string; name: string } | null;
  documents: Array<{ id: string; label: string; original_name: string; sha256: string }>;
  publication_status: PublicationStatus;
  warnings: string[];
}

export type BulkProfileStatus = 'matched' | 'unmatched' | 'ambiguous' | 'conflict' | 'imported' | 'failed';

export interface BulkProfileResult {
  prefix: string;
  files: { fields: boolean; record: boolean; pdf: boolean };
  version?: string | null;
  coverage_tier?: CoverageTier;
  candidate_label?: string | null;
  identity?: { sos_candidate_id: string | null; fec_candidate_id: string | null; full_name: string | null };
  contest?: { office: string | null; district: string | null; election_type: string; election_year: number };
  candidate?: {
    id: string; name: string; office_sought: string | null; district: string | null; status: string | null;
    contest_id: string | null; contest_label: string | null; publication_status: PublicationStatus | null;
  };
  status: BulkProfileStatus;
  reason?: string;
  preflight?: ProfilePreflight;
  summary?: ProfileImportSummary;
}

export interface BulkContestSummary {
  contest_id: string | null;
  label: string;
  tiers: CoverageTier[];
  mixed_tier: boolean;
  matched: number;
  imported: number;
  unmatched: number;
  ambiguous: number;
  conflict: number;
  failed: number;
  unlinked: number;
}

export interface BulkProfileResponse {
  election_year: number;
  dry_run: boolean;
  ignored: string[];
  results: BulkProfileResult[];
  contests: BulkContestSummary[];
}

/** One row per contest in a cycle — the coverage dashboard (§5.4.2). */
export interface ContestCoverage {
  contest_id: string;
  display_label: string;
  election_type: string;
  office_key: string;
  candidates: number;
  loaded: number;
  published: number;
  tiers: CoverageTier[];
}

export interface UnlinkedCoverage {
  office_sought: string;
  candidacies: number;
  profiles: number;
}

/** A ballot-visible Lt. Governor candidacy with no governor link — on no ballot line (§5.9.2). */
export interface UnpairedLtGovernor {
  candidate_id: string;
  person_id: string;
  name: string;
  party: string | null;
}

export interface CoverageResponse {
  election_year: number;
  contests: ContestCoverage[];
  unlinked: UnlinkedCoverage[];
  unpaired_lt_governors: UnpairedLtGovernor[];
}

const bundleForm = (bundle: ExplainerBundle, extra: Record<string, string>) => {
  const form = new FormData();
  if (bundle.fields) form.append('fields', bundle.fields);
  if (bundle.record) form.append('record', bundle.record);
  if (bundle.pdf) form.append('pdf', bundle.pdf);
  for (const [k, v] of Object.entries(extra)) form.append(k, v);
  return form;
};

export async function fetchCandidateProfile(candidateId: string): Promise<CandidateProfileBundleView> {
  const res = await api.get(`/admin/candidates/${candidateId}/profile`);
  return res.data.data as CandidateProfileBundleView;
}

export async function preflightProfile(candidateId: string, bundle: ExplainerBundle, keepFields: string[]): Promise<ProfilePreflight> {
  const res = await api.post(
    `/admin/candidates/${candidateId}/profile-import`,
    bundleForm(bundle, { dry_run: 'true', keep_fields: keepFields.join(',') })
  );
  return res.data.data as ProfilePreflight;
}

export async function importProfile(candidateId: string, bundle: ExplainerBundle, keepFields: string[]): Promise<ProfileImportSummary> {
  const res = await api.post(
    `/admin/candidates/${candidateId}/profile-import`,
    bundleForm(bundle, { keep_fields: keepFields.join(',') })
  );
  return res.data.data as ProfileImportSummary;
}

export async function bulkProfileImport(electionYear: number, files: File[], dryRun: boolean): Promise<BulkProfileResponse> {
  const form = new FormData();
  for (const file of files) form.append('files', file);
  form.append('election_year', String(electionYear));
  form.append('dry_run', dryRun ? 'true' : 'false');
  const res = await api.post('/admin/candidates/profile-import', form);
  return res.data.data as BulkProfileResponse;
}

export async function setProfilePublication(candidateId: string, status: PublicationStatus): Promise<{ candidate_id: string; publication_status: PublicationStatus }> {
  const res = await api.patch(`/admin/candidates/${candidateId}/profile/publication`, { publication_status: status });
  return res.data.data;
}

export async function fetchCoverage(electionYear: number): Promise<CoverageResponse> {
  const res = await api.get('/admin/candidates/profile-coverage', { params: { election_year: electionYear } });
  return res.data.data as CoverageResponse;
}

export async function documentDownloadUrl(candidateId: string, documentId: string): Promise<{ download_url: string; filename: string }> {
  const res = await api.get(`/admin/candidates/${candidateId}/documents/${documentId}/download`, { params: { format: 'json' } });
  return res.data.data;
}

// ── Hand edits (PRD v1.7, O1 revisited) ─────────────────────────────────────

export interface ProfilePatch {
  coverage_tier?: CoverageTier;
  facts_as_of?: string | null;
  profile_version?: string | null;
  who_they_are?: string | null;
  why_running?: string | null;
  top_priorities?: string | null;
  experience_background?: string | null;
  record_in_office?: string | null;
  who_backs_them?: string | null;
  content_provenance?: Partial<Record<ProfileField, { origin: ProvenanceEntry['origin']; tool?: string; source?: string } | null>>;
}

export interface StatementInput {
  source_type: string;
  source_name: string;
  source_url: string | null;
  source_date: string | null;
  question: string | null;
  body: string | null;
  response_status: ResponseStatus;
  display_order: number;
}

export type { EndorsementInput } from '../../constants/endorsements';

export interface PositionInput {
  ballot_initiative_id: string;
  stance: Stance;
  quote: string | null;
  source_type: string | null;
  source_name: string | null;
  source_url: string | null;
  source_date: string;
}

export interface MeasureOption {
  id: string;
  measure_number: string | null;
  popular_name: string | null;
  ballot_title: string | null;
  status: string;
  ballot_order: number | null;
  has_position: boolean;
}

export type ProfileRowKind = 'statements' | 'endorsements' | 'positions';

export async function saveProfile(candidateId: string, patch: ProfilePatch): Promise<void> {
  await api.put(`/admin/candidates/${candidateId}/profile`, patch);
}

export async function fetchMeasureOptions(candidateId: string): Promise<{ cycle: string | null; measures: MeasureOption[] }> {
  const res = await api.get(`/admin/candidates/${candidateId}/profile/measure-options`);
  return res.data.data;
}

/** Add (no rowId) or correct one statement, endorsement or measure position. */
export async function saveProfileRow(
  candidateId: string,
  kind: ProfileRowKind,
  rowId: string | null,
  input: StatementInput | EndorsementInput | PositionInput,
): Promise<void> {
  const base = `/admin/candidates/${candidateId}/profile/${kind}`;
  if (rowId) await api.put(`${base}/${rowId}`, input);
  else await api.post(base, input);
}

export async function deleteProfileRow(candidateId: string, kind: ProfileRowKind, rowId: string): Promise<void> {
  await api.delete(`/admin/candidates/${candidateId}/profile/${kind}/${rowId}`);
}

/** The fields a profile carries, in order, with their HTML (or null). */
export const profileFieldValues = (profile: CandidateProfile): Array<[ProfileField, string | null]> =>
  PROFILE_FIELDS.map((field) => [field, profile[field]]);

const csvCell = (value: unknown): string => {
  const text = value == null ? '' : String(value);
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

/**
 * The October war-room sheet (§5.4.2): one row per contest, then one per
 * office with unlinked candidacies. Pure, so it is testable without a DOM.
 */
export function coverageCsv(coverage: CoverageResponse): string {
  const header = ['Contest', 'Type', 'Office key', 'Candidates on ballot', 'Profiles loaded', 'Profiles published', 'Tier', 'Unlinked candidacies', 'Profiles on unlinked'];
  const rows: string[][] = coverage.contests.map((c) => [
    c.display_label, c.election_type, c.office_key, String(c.candidates), String(c.loaded), String(c.published),
    c.tiers.length > 1 ? `MIXED (${c.tiers.join('/')})` : (c.tiers[0] ?? ''), '', '',
  ]);
  for (const u of coverage.unlinked) {
    rows.push([`(no contest) ${u.office_sought}`, '', '', '', '', '', '', String(u.candidacies), String(u.profiles)]);
  }
  for (const u of coverage.unpaired_lt_governors) {
    rows.push([`(no running mate) ${u.name}${u.party ? ` — ${u.party}` : ''}`, 'general', 'lt_governor', '', '', '', '', '', '']);
  }
  return [header, ...rows].map((r) => r.map(csvCell).join(',')).join('\n');
}

/** Bulk results grouped for the modal: contest summary first, its bundles beneath. */
export function groupBulkResults(response: BulkProfileResponse): Array<{ summary: BulkContestSummary; results: BulkProfileResult[] }> {
  const keyOf = (r: BulkProfileResult) =>
    r.candidate?.contest_id || r.candidate?.contest_label || `${r.contest?.office ?? '?'} (${r.contest?.election_type ?? '?'})`;
  const byKey = new Map<string, BulkProfileResult[]>();
  for (const r of response.results) {
    const key = keyOf(r);
    if (!byKey.has(key)) byKey.set(key, []);
    byKey.get(key)!.push(r);
  }
  return response.contests.map((summary) => {
    const key = summary.contest_id || summary.label;
    const results = byKey.get(key)
      ?? response.results.filter((r) => keyOf(r) === key || (!r.candidate?.contest_id && (r.candidate?.contest_label ?? r.contest?.office) === summary.label));
    return { summary, results };
  });
}
